import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  BOOST,
  INDEED,
  JOVEO,
  POOL,
  TALROO,
  ZIPRECRUITER,
} from '@/types/channels';
export function useChannelDescription() {
  const intl = useIntl();

  const getTitle = useCallback(
    (channelSlug: string, channelName: string) => {
      switch (channelSlug) {
        case INDEED:
          return intl.formatMessage({
            defaultMessage: 'Indeed - Sponsored Job',
            description: 'Title of the Indeed channel for job posting',
          });
        case ZIPRECRUITER:
          return intl.formatMessage({
            defaultMessage: 'ZipRecruiter - Sponsored Job',
            description: 'Title of the ZipRecruiter channel for job posting',
          });
        case TALROO:
          return intl.formatMessage({
            defaultMessage: 'Talroo - Programmatic Job Advertising',
            description: 'Title of the Talroo channel for job posting',
          });
        case JOVEO:
          return intl.formatMessage({
            defaultMessage: 'Joveo - Programmatic Job Advertising',
            description: 'Title of the Joveo channel for job posting',
          });
        case POOL:
          return intl.formatMessage({
            defaultMessage: 'Fountain Pool',
            description: 'Title of the Pool channel for job posting',
          });
        case BOOST:
          return intl.formatMessage({
            defaultMessage: 'Boost My Job',
            description: 'Title of the Boost channel for job posting',
          });
        default:
          return intl.formatMessage(
            {
              defaultMessage: 'Post to {channelName}',
              description:
                'Title of the channel for job posting, {channelName} is the name of the channel',
            },
            { channelName },
          );
      }
    },
    [intl],
  );

  const getDescription = useCallback(
    (channelSlug: string) => {
      switch (channelSlug) {
        case INDEED:
          return intl.formatMessage({
            defaultMessage:
              "Indeed is the leading global job site that helps people get jobs. An Indeed Sponsored Job is a paid job ad that appears in relevant search results for job seekers, ensuring your role stays visible at the top of results rather than falling back as new postings come in. Sponsored Jobs include advanced targeting, AI matching tools, and attention-grabbing visuals like company logos that help you stand out from similar roles. The degree to which a job is promoted will depend on the chosen budget — the higher the budget, the more qualified candidates will see your advertisement, increasing your likelihood of finding the right hire within a period of 30 days. By posting your job to Indeed, you agree to Indeed's Terms of Service: https://www.indeed.com/legal.",
            description: 'Description of the Indeed channel',
          });
        case ZIPRECRUITER:
          return intl.formatMessage({
            defaultMessage:
              'ZipRecruiter is an international generic job board. It is a thriving marketplace where millions of people across the U.S. find jobs. The product consists of a single Boost Posting designed to match your role with top quality candidates using award-winning, machine learning technology. Your vacancy will be posted to the appropriate destination site, based on the submitted working location of the vacancy, and remains live for a period of 30 days.',
            description: 'Description of the ZipRecruiter channel',
          });
        case TALROO:
          return intl.formatMessage({
            defaultMessage:
              "Talroo is a data-driven programmatic job advertising platform that uses real-time bidding and rich targeting to place your jobs where the right candidates are already browsing — whether on niche job boards, social channels, mobile apps, or search results. Powered by AI, Talroo's talent matching marketplace helps employers and candidates connect through more than one billion job matches each month, with a particular strength in high-volume hiring for frontline and skilled trade roles across industries such as retail, food service, healthcare, logistics, transportation, and manufacturing. Unlike traditional distribution vendors, Talroo is a first-party sourcing solution that uses consumer advertising technology, similar to Google and Facebook, to find unique, active candidates you won't get with any other solution within a period of 30 days.",
            description: 'Description of the Talroo channel',
          });
        case JOVEO:
          return intl.formatMessage({
            defaultMessage:
              "Joveo is the global leader in programmatic job advertising, helping employers source the right talent faster and more efficiently through AI-powered media buying. Powered by Joveo's MOJO Pro platform, your jobs are automatically distributed and continuously optimized across a vast network of global, local, niche, community, and DE&I publishers — including search, social, and display channels such as Indeed, LinkedIn, Glassdoor, Google for Jobs and many more within a period of 30 days. Its programmatic engine tracks performance from click to hire across every source and automatically shifts budget toward high-performing publishers, helping drive candidate traffic up and advertising costs down.",
            description: 'Description of the Joveo channel',
          });
        case POOL:
          return intl.formatMessage({
            defaultMessage:
              "Fountain Pool is a built-in candidate rediscovery and rehire engine that helps you tap into everyone who's ever applied, referred, or worked for you so you can rehire faster and more efficiently. Rather than starting every requisition from scratch and paying again to source the same kind of candidate, Pool automatically surfaces, scores, and re-engages your existing talent — turning past applicants, silver medalists, former workers, and referrals into your fastest, lowest-cost hiring channel. Pool runs in the background, continuously scanning your database to find high-potential candidates based on location, role, and urgency. When a hiring need arises, Pool automatically kicks off personalized outreach, restarting the conversation with people who already know your brand and are ready to work. Purpose-built for frontline and high-volume use cases such as seasonal and event-based rehiring, re-engaging past applicants who weren't hired, and bringing back trusted former workers, Pool helps shorten time-to-fill, reduce sourcing spend, and boost retention — all without bloated setup or campaign configuration.",
            description: 'Description of the Pool channel',
          });
        case BOOST:
          return intl.formatMessage({
            defaultMessage:
              "Fountain Boost amplifies your hiring reach by automatically publishing your jobs across thousands of job boards, niche sites, and candidate networks — all from a single posting. Powered by performance optimization, Boost continuously analyzes results and shifts distribution toward the highest-quality sources for your role, location, and industry. Whether you're hiring for frontline, skilled trades, or high-volume roles, Boost ensures your vacancy gets in front of the right candidates at the right time, within a period of 30 days.",
            description: 'Description of the Boost channel',
          });
        default:
          return null;
      }
    },
    [intl],
  );

  return { getDescription, getTitle };
}
